import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lock, Download, LogOut, Database, BarChart3, MapPin, Check, XCircle } from "lucide-react";
import { API } from "./constants";

export default function AdminPage({ onBack }) {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [pendingPois, setPendingPois] = useState([]);
  const [processingPoi, setProcessingPoi] = useState("");

  // Verify existing token on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/analytics/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { setVerified(true); fetchStats(); fetchPendingPois(); })
      .catch(() => { setToken(""); sessionStorage.removeItem("admin_token"); });
  }, []);

  const fetchStats = async () => {
    try {
      const resp = await axios.get(`${API}/analytics/community-stats`);
      setStats(resp.data);
    } catch (err) {
      console.error("Failed to fetch admin stats:", err);
    }
  };

  const fetchPendingPois = async () => {
    try {
      const t = sessionStorage.getItem("admin_token");
      const resp = await axios.get(`${API}/pois/pending`, { headers: { Authorization: `Bearer ${t}` } });
      setPendingPois(resp.data.pois || []);
    } catch (err) {
      console.error("Failed to fetch pending POIs:", err);
    }
  };

  const handleApprovePoi = async (id) => {
    setProcessingPoi(id);
    try {
      const t = sessionStorage.getItem("admin_token");
      await axios.post(`${API}/pois/approve/${id}`, {}, { headers: { Authorization: `Bearer ${t}` } });
      setPendingPois(prev => prev.filter(p => p.id !== id));
    } catch { setError("Failed to approve"); }
    finally { setProcessingPoi(""); }
  };

  const handleRejectPoi = async (id) => {
    setProcessingPoi(id);
    try {
      const t = sessionStorage.getItem("admin_token");
      await axios.post(`${API}/pois/reject/${id}`, { reason: "" }, { headers: { Authorization: `Bearer ${t}`, "Content-Type": "application/json" } });
      setPendingPois(prev => prev.filter(p => p.id !== id));
    } catch { setError("Failed to reject"); }
    finally { setProcessingPoi(""); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const resp = await axios.post(`${API}/analytics/admin/login`, { email, password });
      const t = resp.data.token;
      setToken(t);
      sessionStorage.setItem("admin_token", t);
      setVerified(true);
      setPassword("");
      fetchStats();
      fetchPendingPois();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const handleLogout = () => {
    setToken(""); setVerified(false);
    sessionStorage.removeItem("admin_token");
  };

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const t = sessionStorage.getItem("admin_token");
      const resp = await fetch(`${API}/analytics/admin/export/${type}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = resp.headers.get("content-disposition")?.split("filename=")[1] || `drivespend_${type}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { setError("Download failed. Try logging in again."); }
    finally { setDownloading(""); }
  };

  // Login screen
  if (!verified) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <Lock className="w-10 h-10 text-[#E52020] mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white font-['Manrope'] tracking-wider">ADMIN ACCESS</h1>
            <p className="text-[#666666] text-sm mt-1 font-['Manrope']">DRIVE$PEND Analytics</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              className="w-full bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg px-4 py-3 text-white placeholder-[#4A5568] text-sm font-mono focus:outline-none focus:border-[#E52020]"
              data-testid="admin-email-input"
            />
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg px-4 py-3 text-white placeholder-[#4A5568] text-sm font-mono focus:outline-none focus:border-[#E52020]"
              data-testid="admin-password-input"
            />
            {error && <p className="text-[#DC2626] text-xs font-mono">{error}</p>}
            <button
              type="submit" disabled={loading || !email || !password}
              className="w-full bg-[#E52020] hover:bg-[#CC1B1B] disabled:bg-[#2A3A4E] text-white font-['Manrope'] font-bold tracking-wider py-3 rounded-lg transition-colors text-sm"
              data-testid="admin-login-btn"
            >
              {loading ? "AUTHENTICATING..." : "SIGN IN"}
            </button>
          </form>
          <button onClick={onBack} className="mt-6 text-[#4A5568] hover:text-[#666666] text-xs font-['Manrope'] tracking-wider w-full text-center">
            BACK TO APP
          </button>
        </div>
      </div>
    );
  }

  // Admin panel
  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-bold font-['Manrope'] tracking-wider text-white">DRIVE$PEND ADMIN</h1>
            <p className="text-[#4A5568] text-xs font-mono mt-1">Analytics Data Export</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-xs text-[#4A5568] hover:text-[#666666] font-['Manrope'] tracking-wider px-3 py-2 border border-[#2A3A4E] rounded-lg">
              APP
            </button>
            <button onClick={handleLogout} className="text-xs text-[#DC2626] hover:text-[#EF4444] font-['Manrope'] tracking-wider flex items-center gap-1.5 px-3 py-2 border border-[#2A3A4E] rounded-lg" data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5" />LOGOUT
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg p-4">
              <div className="text-[10px] font-['Manrope'] text-[#4A5568] tracking-wider mb-1">TOTAL TRIPS</div>
              <div className="text-2xl font-mono font-bold text-[#E52020]">{stats.total_trips.toLocaleString()}</div>
            </div>
            <div className="bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg p-4">
              <div className="text-[10px] font-['Manrope'] text-[#4A5568] tracking-wider mb-1">AVG COST</div>
              <div className="text-2xl font-mono font-bold text-white">${stats.avg_cost}</div>
            </div>
            <div className="bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg p-4">
              <div className="text-[10px] font-['Manrope'] text-[#4A5568] tracking-wider mb-1">THIS WEEK</div>
              <div className="text-2xl font-mono font-bold text-[#16A34A]">{stats.trips_this_week}</div>
            </div>
          </div>
        )}

        {/* Usage Stats */}
        {stats?.usage && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg p-4">
              <div className="text-[10px] font-['Manrope'] text-[#4A5568] tracking-wider mb-1">TOTAL VISITS</div>
              <div className="text-2xl font-mono font-bold text-[#8B5CF6]">{stats.usage.total_visits.toLocaleString()}</div>
            </div>
            <div className="bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg p-4">
              <div className="text-[10px] font-['Manrope'] text-[#4A5568] tracking-wider mb-1">LOGGED IN</div>
              <div className="text-2xl font-mono font-bold text-[#E52020]">{stats.usage.logged_in.toLocaleString()}</div>
            </div>
            <div className="bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg p-4">
              <div className="text-[10px] font-['Manrope'] text-[#4A5568] tracking-wider mb-1">GUEST</div>
              <div className="text-2xl font-mono font-bold text-[#666666]">{stats.usage.guest.toLocaleString()}</div>
            </div>
          </div>
        )}

        {/* Download Buttons */}
        <div className="space-y-4">
          <h2 className="text-xs font-['Manrope'] text-[#4A5568] tracking-widest mb-2">DATA EXPORTS</h2>

          <button
            onClick={() => handleDownload("trips")}
            disabled={!!downloading}
            className="w-full bg-[#1A1A1A] hover:bg-[#223044] border border-[#2A3A4E] rounded-lg p-5 flex items-center gap-4 transition-colors group disabled:opacity-50"
            data-testid="admin-download-trips"
          >
            <div className="w-12 h-12 rounded-lg bg-[#E52020]/10 flex items-center justify-center group-hover:bg-[#E52020]/20 transition-colors">
              <Database className="w-6 h-6 text-[#E52020]" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-['Manrope'] font-bold text-white tracking-wide">ALL TRIP DATA</div>
              <div className="text-xs text-[#4A5568] font-mono mt-0.5">Every anonymous trip — distance, cost, fuel type, route, RUC, tolls, ferry</div>
            </div>
            <div className="flex items-center gap-2 text-[#E52020]">
              {downloading === "trips" ? (
                <span className="text-xs font-mono animate-pulse">Downloading...</span>
              ) : (
                <><Download className="w-5 h-5" /><span className="text-xs font-['Manrope'] font-bold">CSV</span></>
              )}
            </div>
          </button>

          <button
            onClick={() => handleDownload("summary")}
            disabled={!!downloading}
            className="w-full bg-[#1A1A1A] hover:bg-[#223044] border border-[#2A3A4E] rounded-lg p-5 flex items-center gap-4 transition-colors group disabled:opacity-50"
            data-testid="admin-download-summary"
          >
            <div className="w-12 h-12 rounded-lg bg-[#16A34A]/10 flex items-center justify-center group-hover:bg-[#16A34A]/20 transition-colors">
              <BarChart3 className="w-6 h-6 text-[#16A34A]" />
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-['Manrope'] font-bold text-white tracking-wide">SUMMARY REPORT</div>
              <div className="text-xs text-[#4A5568] font-mono mt-0.5">Averages, region breakdown, popular routes — ready for analysis</div>
            </div>
            <div className="flex items-center gap-2 text-[#16A34A]">
              {downloading === "summary" ? (
                <span className="text-xs font-mono animate-pulse">Downloading...</span>
              ) : (
                <><Download className="w-5 h-5" /><span className="text-xs font-['Manrope'] font-bold">CSV</span></>
              )}
            </div>
          </button>
        </div>

        {error && <p className="text-[#DC2626] text-xs font-mono mt-4">{error}</p>}

        {/* Pending POI Submissions */}
        <div className="mt-8 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-['Manrope'] text-[#4A5568] tracking-widest">PENDING LOCATION SUBMISSIONS</h2>
            {pendingPois.length > 0 && <span className="bg-[#E52020] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{pendingPois.length}</span>}
          </div>

          {pendingPois.length === 0 ? (
            <p className="text-[#2A3A4E] text-xs font-mono text-center py-6">No pending submissions</p>
          ) : (
            <div className="space-y-3">
              {pendingPois.map(poi => (
                <div key={poi.id} className="bg-[#1A1A1A] border border-[#2A3A4E] rounded-lg p-4" data-testid={`pending-poi-${poi.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-[#E52020] flex-shrink-0" />
                        <span className="text-sm font-bold text-white truncate">{poi.name}</span>
                        <span className="text-[9px] font-['Manrope'] text-[#4A5568] tracking-wider bg-[#0A0F1A] px-2 py-0.5 rounded flex-shrink-0">{(poi.type || "").replace(/_/g, " ").toUpperCase()}</span>
                      </div>
                      {poi.description && <p className="text-xs text-[#4A5568] font-mono ml-6 mb-1">{poi.description}</p>}
                      <p className="text-[10px] text-[#2A3A4E] font-mono ml-6">By: {poi.submitted_by || "Unknown"} | {poi.lat?.toFixed(4)}, {poi.lon?.toFixed(4)} | {poi.created_at?.split("T")[0]}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleApprovePoi(poi.id)}
                        disabled={processingPoi === poi.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#16A34A]/10 border border-[#16A34A]/30 rounded text-[#16A34A] text-xs font-['Manrope'] font-bold hover:bg-[#16A34A]/20 transition-colors disabled:opacity-50"
                        data-testid={`approve-poi-${poi.id}`}
                      >
                        <Check className="w-3.5 h-3.5" />APPROVE
                      </button>
                      <button
                        onClick={() => handleRejectPoi(poi.id)}
                        disabled={processingPoi === poi.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#DC2626]/10 border border-[#DC2626]/30 rounded text-[#DC2626] text-xs font-['Manrope'] font-bold hover:bg-[#DC2626]/20 transition-colors disabled:opacity-50"
                        data-testid={`reject-poi-${poi.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5" />REJECT
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[#2A3A4E] text-[10px] text-center mt-12 font-mono">All exported data is fully anonymous — no user IDs or IP addresses</p>
      </div>
    </div>
  );
}
